import { useCallback, useEffect, useState } from "react";
import api from "../../lib/api";

const locCache = {};
const LOC_KEY = "bond.nav.location";
const pinKey = (user) => `bond.nav.pins.${user?.id || user?.email || "anon"}`;

export function useNavLocations(user) {
  const uid = user?.id || user?.email || "";
  const [locations, setLocations] = useState(locCache[uid] || []);
  useEffect(() => {
    setLocations(locCache[uid] || []);
    if (!uid || locCache[uid] || user.role === "finance") return;
    api.get("/locations/overview").then(({ data }) => {
      locCache[uid] = (data.locations || []).map((l) => l.location);
      setLocations(locCache[uid]);
    }).catch(() => {});
  }, [uid]); // eslint-disable-line react-hooks/exhaustive-deps
  return locations;
}

export function useNavLocation(locations) {
  const [loc, setLoc] = useState(() => localStorage.getItem(LOC_KEY) || "");
  useEffect(() => {
    if (loc && locations.length && !locations.includes(loc)) setLoc("");
  }, [loc, locations]);
  const set = useCallback((v) => {
    if (v) localStorage.setItem(LOC_KEY, v); else localStorage.removeItem(LOC_KEY);
    setLoc(v);
    window.dispatchEvent(new Event("nav-location-changed"));
  }, []);
  useEffect(() => {
    const sync = () => setLoc(localStorage.getItem(LOC_KEY) || "");
    window.addEventListener("nav-location-changed", sync);
    return () => window.removeEventListener("nav-location-changed", sync);
  }, []);
  return [locations.includes(loc) ? loc : "", set];
}

export function usePins(user) {
  const read = () => { try { return JSON.parse(localStorage.getItem(pinKey(user)) || "[]"); } catch { return []; } };
  const [pins, setPins] = useState(read);
  useEffect(() => {
    const sync = () => setPins(read());
    window.addEventListener("nav-pins-changed", sync);
    return () => window.removeEventListener("nav-pins-changed", sync);
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggle = useCallback((to) => {
    const cur = read();
    const next = cur.includes(to) ? cur.filter((p) => p !== to) : [...cur, to];
    localStorage.setItem(pinKey(user), JSON.stringify(next));
    window.dispatchEvent(new Event("nav-pins-changed"));
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps
  return [pins, toggle];
}
